import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { MarkdownRenderer } from '../../components/ui/MarkdownRenderer'
import { PermissionGate } from '../../components/PermissionGate'
import { AttachmentList } from '../../components/AttachmentList'
import { DocumentLinkPicker } from '../../components/DocumentLinkPicker'
import { documentationApi } from '../../api/documentation.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import styles from './Documentation.module.css'

const CATEGORY_SUGGESTIONS = [
  'Architektur',
  'Feature-Spezifikation',
  'API-Dokumentation',
  'Datenbankstruktur',
  'Entwicklungsrichtlinien',
  'Installation',
  'Testprotokolle',
  'Changelog',
  'Brainstorming',
]

export function DocumentEditor({ documentId, projectId, onDeleted }: { documentId: string; projectId: string; onDeleted: () => void }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [showHistory, setShowHistory] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [content, setContent] = useState('')

  const docQuery = useQuery({ queryKey: ['documentation', documentId], queryFn: async () => (await documentationApi.get(documentId)).data })
  const historyQuery = useQuery({
    queryKey: ['documentation', documentId, 'versions'],
    queryFn: async () => (await documentationApi.listVersions(documentId)).data,
    enabled: showHistory,
  })

  useEffect(() => {
    if (docQuery.data) {
      setTitle(docQuery.data.title)
      setCategory(docQuery.data.category ?? '')
      setContent(docQuery.data.content)
    }
  }, [docQuery.data])

  // DocumentEditor wird beim Dokumentwechsel wiederverwendet statt neu gemountet (kein `key`
  // in DocumentationTab), daher muss transienter UI-Status hier explizit zurückgesetzt werden.
  // Ohne das blieb z.B. ein offener Lösch-Dialog nach dem Löschen bestehen und fragte beim
  // automatischen Wechsel zum nächsten Dokument fälschlich erneut nach dessen Löschung.
  useEffect(() => {
    setMode('view')
    setShowHistory(false)
    setConfirmDelete(false)
  }, [documentId])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documentation'], exact: false })

  const saveMutation = useMutation({
    mutationFn: () => documentationApi.update(documentId, { title, category, content }),
    onSuccess: () => {
      invalidate()
      toast.success('Dokument gespeichert.')
      setMode('view')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => documentationApi.remove(documentId),
    onSuccess: () => {
      invalidate()
      toast.success('Dokument gelöscht.')
      onDeleted()
    },
  })

  const linksMutation = useMutation({
    mutationFn: (targetIds: string[]) => documentationApi.setLinks(documentId, targetIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documentation', documentId] }),
  })

  const doc = docQuery.data
  if (!doc) return null

  const linkedIds = doc.linkedDocuments.map((d) => d.id)

  return (
    <Card>
      <div className={styles.toolbar}>
        {mode === 'edit' ? (
          <Input value={title} onChange={(e) => setTitle(e.target.value)} style={{ fontWeight: 700, fontSize: 'var(--text-lg)' }} />
        ) : (
          <h2 style={{ margin: 0 }}>{doc.title}</h2>
        )}
        <PermissionGate anyOf={['documentation.edit']}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {mode === 'view' ? (
              <Button size="sm" variant="secondary" onClick={() => setMode('edit')}>
                Bearbeiten
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
                  Abbrechen
                </Button>
                <Button size="sm" variant="primary" loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                  Speichern
                </Button>
              </>
            )}
            <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
              Löschen
            </Button>
          </div>
        </PermissionGate>
      </div>

      {mode === 'edit' ? (
        <Field label="Kategorie" htmlFor="doc-category">
          <Select id="doc-category" value={category} onChange={(e) => setCategory(e.target.value)} style={{ maxWidth: 260, marginBottom: 'var(--space-3)' }}>
            <option value="">Keine Kategorie</option>
            {CATEGORY_SUGGESTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
      ) : doc.category ? (
        <div style={{ marginBottom: 'var(--space-3)', color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{doc.category}</div>
      ) : null}

      {mode === 'edit' ? (
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} style={{ minHeight: 320, fontFamily: 'var(--font-mono)' }} />
      ) : (
        <MarkdownRenderer content={doc.content} />
      )}

      <div style={{ marginTop: 'var(--space-5)', borderTop: '1px solid var(--surface-border)', paddingTop: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
          Verlinkte Dokumente
        </div>
        <DocumentLinkPicker
          projectId={projectId}
          excludeId={documentId}
          linkedIds={linkedIds}
          onToggle={(id) => {
            const next = linkedIds.includes(id) ? linkedIds.filter((x) => x !== id) : [...linkedIds, id]
            linksMutation.mutate(next)
          }}
        />
      </div>

      <div style={{ marginTop: 'var(--space-5)', borderTop: '1px solid var(--surface-border)', paddingTop: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
          Anhänge
        </div>
        <AttachmentList parentType="document" parentId={documentId} />
      </div>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <Button size="sm" variant="ghost" onClick={() => setShowHistory((v) => !v)}>
          {showHistory ? 'Historie ausblenden' : `Versionshistorie anzeigen (${doc._count.versions})`}
        </Button>
        {showHistory ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'var(--space-2)' }}>
            {historyQuery.data?.map((version) => (
              <details key={version.id} style={{ fontSize: 'var(--text-sm)' }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  {new Date(version.createdAt).toLocaleString('de-DE')} · {version.editor.displayName}
                </summary>
                <div style={{ marginTop: 4 }}>
                  <MarkdownRenderer content={version.content} />
                </div>
              </details>
            ))}
            {historyQuery.data?.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Noch keine älteren Versionen.</span> : null}
          </div>
        ) : null}
      </div>

      {confirmDelete ? (
        <ConfirmDialog
          title="Dokument löschen"
          message={`"${doc.title}" wirklich löschen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Card>
  )
}
